import { useMemo, FC } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { updateSongManuallyAction } from 'app/store/SongList/slice';
import { SongStatusType } from 'types/song';
import { updateSongEffect } from 'app/store/SongList/effects';
import {
  getAudioPlayer,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { getSong, getSongFormData } from 'app/store/SongList/selectors';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { JSONSchema7 } from 'json-schema';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { SongFormData } from './types';
import { SongFormComponent } from './SongFormComponent';

interface SongFormProps {
  validationSchema: JSONSchema7;
}

export const SongForm: FC<SongFormProps> = ({ validationSchema }) => {
  const dispatch = useAppDispatch();
  const { loadAudio, playPauseAudio } = useAudioPlayer();
  const songFormData = useAppSelector(getSongFormData);
  const song = useAppSelector(getSong);

  const canPlayLatest = Boolean(song?.latestVersionAudio);
  const { isPlaying } = useAppSelector(getAudioPlayer);
  const currentAudio = useAppSelector(getPlayerCurrentAudio);

  let maxTitleLength;
  if (typeof validationSchema?.properties?.title !== 'boolean') {
    maxTitleLength = validationSchema?.properties?.title?.maxLength;
  }

  const yupValidationSchema = useMemo(
    () =>
      validationSchema && transformSchemaToYup(validationSchema, songFormData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema]
  );

  const onSongUpdate = async (data: Partial<SongFormData>) => {
    dispatch(updateSongEffect({ formData: data }));
  };

  const onSongStatusChange = (status: SongStatusType) =>
    dispatch(updateSongManuallyAction(song ? { ...song, status } : undefined));

  const handlePlay = async () => {
    if (song?.latestVersionAudio) {
      if (currentAudio?.audioGuid === song.latestVersionAudio) {
        playPauseAudio();
      } else {
        loadAudio({
          audio: [
            {
              audioGuid: song.latestVersionAudio,
              parentGuid: song.guid,
              songStatus: song.status,
              songTitle: song.title,
            },
          ],
        });
      }
    }
  };

  return (
    <SongFormComponent
      songFormData={songFormData}
      onSongUpdate={onSongUpdate}
      onSongStatusChange={onSongStatusChange}
      handlePlay={handlePlay}
      isPlaying={
        isPlaying && currentAudio?.audioGuid === song?.latestVersionAudio
      }
      isLoading={
        currentAudio?.audioGuid === song?.latestVersionAudio &&
        currentAudio?.loading
      }
      canPlayLatest={canPlayLatest}
      validationSchema={yupValidationSchema}
      maxTitleLength={maxTitleLength}
    />
  );
};
